// Purpose: FilterBar primitive — the row above list-view tables.
// Includes SearchInput (leading magnifier icon), inline FilterDropdowns,
// and an optional right-aligned action slot for the primary CTA ("+
// New Share", "+ New Dropbox"). Consumers compose the filters they
// need; the bar itself is purely layout.

import type { InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';
import { ChevronDownIcon, SearchIcon } from '../icons/index.tsx';

export interface FilterBarProps {
  children?: ReactNode;
  /** Right-aligned action slot. Typically a primary Button. */
  actions?: ReactNode;
  className?: string;
}

export function FilterBar({ children, actions, className }: FilterBarProps) {
  return (
    <div className={['flex flex-wrap items-center gap-3', className ?? ''].join(' ')}>
      <div className="flex flex-1 flex-wrap items-center gap-3 min-w-0">{children}</div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'className'> {
  className?: string;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { className, placeholder = 'Search…', ...rest },
  ref
) {
  return (
    <div className={['relative flex items-center', 'min-w-[240px]', className ?? ''].join(' ')}>
      <SearchIcon
        size={16}
        className="absolute left-3 text-fg-muted pointer-events-none"
        aria-hidden
      />
      <input
        ref={ref}
        type="search"
        placeholder={placeholder}
        className={[
          'w-full h-10 pl-10 pr-3',
          'bg-bg-secondary border border-border-default rounded-md',
          'text-body-m text-fg-primary placeholder:text-fg-muted',
          'transition-colors duration-fast ease-out',
          'hover:border-border-strong',
          'focus:border-accent-primary focus:outline-none',
        ].join(' ')}
        {...rest}
      />
    </div>
  );
});

export interface FilterOption {
  value: string;
  label: ReactNode;
}

export interface FilterDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: FilterOption[];
  /** Rendered before the current selection (e.g., "Status:"). */
  prefix?: ReactNode;
  className?: string;
  'aria-label'?: string;
}

export function FilterDropdown({
  value,
  onChange,
  options,
  prefix,
  className,
  'aria-label': ariaLabel,
}: FilterDropdownProps) {
  return (
    <div className={['relative inline-flex items-center', className ?? ''].join(' ')}>
      {prefix ? (
        <span className="absolute left-3 text-body-s text-fg-muted pointer-events-none z-10">
          {prefix}
        </span>
      ) : null}
      <select
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={[
          'appearance-none h-10',
          prefix ? 'pl-[4.5rem]' : 'pl-3',
          'pr-9',
          'bg-bg-secondary border border-border-default rounded-md',
          'text-body-m text-fg-primary',
          'transition-colors duration-fast ease-out',
          'hover:border-border-strong',
          'focus:border-accent-primary focus:outline-none',
          'cursor-pointer',
        ].join(' ')}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {typeof o.label === 'string' ? o.label : o.value}
          </option>
        ))}
      </select>
      <ChevronDownIcon
        size={16}
        className="absolute right-3 text-fg-muted pointer-events-none"
        aria-hidden
      />
    </div>
  );
}
